import { Box, Button, Editable, EditablePreview, EditableTextarea, Text } from '@chakra-ui/react';
import { DragHandleIcon } from '@chakra-ui/icons';
import { ReactSortable } from 'react-sortablejs';
import React, { useState, useEffect } from 'react';
import { composeBullet, getDivider, getEndDate, getStartDate } from '../utils';
import { RiCircleFill } from 'react-icons/ri';
import { getUsefulVars, setUcfirst } from '../../../handlers/helpers';
import { updateBulletData } from '../../../formElements/Bullets/utils';
import { freeFormEdit } from '../utils';
import EditButton from '../components/EditButton';

const MONTHS = {
  january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
  july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
};

const getStartDateValue = (item) => {
  const year = parseInt(item.start_year, 10);
  if (!year || isNaN(year)) return null;

  let month = 0;
  if (item.start_month) {
    const parsed = parseInt(item.start_month, 10);
    if (!isNaN(parsed)) {
      month = parsed;
    } else {
      month = MONTHS[item.start_month.toLowerCase()] || 0;
    }
  }
  return year * 100 + month;
};

const sortChronologically = (items) => {
  return [...items].sort((a, b) => {
    const aVal = getStartDateValue(a);
    const bVal = getStartDateValue(b);
    if (aVal === null && bVal === null) return 0;
    if (aVal === null) return 1;
    if (bVal === null) return -1;
    return bVal - aVal;
  });
};

const ensureIds = (items) =>
  items.map((item, index) => ({
    ...item,
    id: item.id || `job-${index}`,
  }));

const PreviewWork = ({ items, props }) => {
  const sectionName = 'professional-experience';
  const buttonLabel = 'Enhance';
  const uv = getUsefulVars(props);
  const jobSeekerData = uv.formikProps.values.jobSeekerData;
  const existingBulletNotice = '';

  const [jobList, setJobList] = useState(() => ensureIds(items));

  // Sync if items change from outside (e.g. a job is added/removed)
  useEffect(() => {
    setJobList(ensureIds(items));
  }, [items]);

  const persistJobs = (newList) => {
    const resumes = uv.stateProps.dataToSave.saved.resumes;
    resumes[uv.resumeIndex]['professional-experience'] = newList;
  };

  const handleSetJobList = (newState) => {
    setJobList(newState);
    persistJobs(newState);
  };

  const handleSortChronologically = () => {
    const sorted = sortChronologically(jobList);
    setJobList(sorted);
    persistJobs(sorted);
  };

  return (
    <Box className="contentBox">

      <ReactSortable
        list={jobList}
        setList={handleSetJobList}
        handle=".job-drag-handle"
        group="jobs"
        filter=".chakra-editable__input, .chakra-editable__textarea, [contenteditable]"
        preventOnFilter={true}
      >
        {jobList.map((item, sectionIndex) => {
          const city = item.city !== undefined ? item.city : '';
          const state = item.state !== undefined ? item.state : '';
          const address = item.location === 'inperson' ? city + getDivider(city, state, ', ') + state : 'Remote';
          return (
            <Box className="subSections" key={item.id}>
              <Box className="subSectionsInterior">
                <Box className="subSection subSectionPE">
                  <Box>
                    <Box className="name">{item.company_name}</Box>
                    <Box display="flex" alignItems="center" marginTop="1px">
                      <DragHandleIcon
                        className="job-drag-handle"
                        sx={{
                          color: 'gray.400',
                          cursor: 'grab',
                          fontSize: '1.2em',
                          marginRight: '0.5em',
                        }}
                      />
                      <Box className="jobTitle">{item.job_title}</Box>
                    </Box>
                  </Box>
                </Box>
                <Box minWidth='20%'>
                  <Box className="address">
                    <Box className="alignRight">{setUcfirst(address)}</Box>
                  </Box>
                  <Box className="dates" minWidth='20%'>
                    <Text className="startDate">{getStartDate(item)}</Text>
                    <Text className="endDate">{getEndDate(item)}</Text>
                  </Box>
                </Box>
              </Box>
              <Box>
                <Box>
                  {item['existing'] !== undefined && item['bulletsb'] === undefined && (
                    <Box>
                      {existingBulletNotice}
                      {Object.values(item['existing']).map((bullet) => {
                        const compositedBullet = composeBullet(bullet);
                        return (
                          <Box className="bulletsWrap">
                            <Text className="bulletIcon"><RiCircleFill /></Text>
                            <Text className="bulletText">{compositedBullet}</Text>
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {item['bulletsb'] !== undefined && (
                    <ReactSortable
                      list={item['bulletsb']}
                      setList={(newState) => {
                        const resumes = uv.stateProps.dataToSave.saved.resumes;
                        resumes[uv.resumeIndex]['professional-experience'][sectionIndex]['bulletsb'] = newState;
                        item['bulletsb'] = newState;
                        updateBulletData({ selectedBullets: newState }, uv.stateProps);
                      }}
                      handle=".drag-handle"
                      group="bullets"
                      filter=".chakra-editable__input, .chakra-editable__textarea, [contenteditable]"
                      preventOnFilter={true}
                    >
                      {Object.values(item['bulletsb']).map((bullet) => {
                        const compositedBullet = composeBullet(bullet);
                        return (
                          <Editable
                            key={bullet.key ? bullet.key : compositedBullet}
                            className='editableWork'
                            autoResize
                            defaultValue={compositedBullet}
                          >
                            <Text className="bulletIcon">
                              <DragHandleIcon
                                className="drag-handle"
                                sx={{
                                  color: 'ORANGE_TEXT',
                                  alignSelf: 'center',
                                  cursor: 'grab',
                                  fontSize: '2em',
                                }}
                              />
                            </Text>
                            <EditablePreview />
                            <EditableTextarea onBlur={(e) => freeFormEdit(e, bullet, sectionIndex, uv, item)} />
                          </Editable>
                        );
                      })}
                    </ReactSortable>
                  )}
                  <EditButton props={props} buttonLabel={buttonLabel} target={sectionName} sectionIndex={sectionIndex} />
                </Box>
              </Box>
            </Box>
          );
        })}
      </ReactSortable>
    </Box>
  );
};
export default PreviewWork;

/*
<Box display="flex"justifyContent="flex-end" mb={2}>
        <Button
          size="sm"
          width='220px'
          variant="outline"
          onClick={handleSortChronologically}
          title="Sort positions by start date, most recent first. Positions without a start date will be moved to the bottom."
        >
          Order Chronologically
        </Button>
      </Box>
 */